/* eslint-disable @typescript-eslint/no-explicit-any */
import { useIntl } from 'react-intl';
import { Form, Row, Col, FormInstance, Input } from 'antd';
import FloatingFormItem from '@/components/_common/FloatingFormItem';
import { useDefaultEmptyFieldError } from '@/hooks';
import {
  BankAccountInfiniteSelectField,
  DocumentTypesInfiniteSelectField,
  InputDate,
  InputDecimal,
  PlanAccountsSelect,
} from '@/components/_common';
import {
  PlanAccountDestination,
  PlanAccountHierarchy,
} from '@/core/domain/enterprise/PlanAccounts/PlanAccounts.types';
import { DirectSaleFinancialFormValues } from '../DirectSaleFinancialTabs';
import {
  DirectSale,
  DirectSaleStatusEnum,
} from '@/core/domain/sales/DirectSale/DirectSale.types';
import moment from 'moment';
import { CompanySettingsInterface } from '@/core/domain/enterprise/Companies/Companies.types';
import PaymentConditionInfiniteSelect from '@/components/_common/PaymentConditionInfiniteSelect';
import { DocumentTypePaymentType } from '@/core/domain/enterprise/DocumentTypes/DocumentTypes.types';
import CostCenterInfiniteSelectField from '@/components/_common/CostCenterInfiniteSelectField';
import { useDocumentType } from '@/core/domain/enterprise/DocumentTypes/DocumentTypes.hooks';
import { useEffect } from 'react';

interface Props {
  form: FormInstance<DirectSaleFinancialFormValues>;
  directSale?: DirectSale;
  totalValues?: { totalDirectSale: number; totalDirectSalaeAccounts: number };
  isStone: boolean;
  companySetting?: CompanySettingsInterface;
  paymentType?: DocumentTypePaymentType;
}

function FinancialDirectSaleForm({
  form,
  directSale,
  totalValues,
  isStone,
  companySetting,
  paymentType,
}: Props) {
  const intl = useIntl();

  const isPaymentTypeCredit = paymentType === DocumentTypePaymentType.Credit;

  const defaultEmptyFieldError = useDefaultEmptyFieldError();

  const isCanceled = directSale?.status === DirectSaleStatusEnum.CANCELED;

  const documentTypeId = Form.useWatch('documentTypeId', form);

  const documentTypeDetail = useDocumentType(documentTypeId);

  useEffect(() => {
    if (documentTypeDetail.data) {
      form.setFieldValue(
        'bankAccountId',
        documentTypeDetail.data.bankAccountId,
      );
    }
  }, [documentTypeDetail.dataUpdatedAt]);

  return (
    <>
      <Form layout="vertical" form={form}>
        <Row gutter={[16, 9]}>
          <Col span={24} sm={24} md={6}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'directSale.financial.form.planAccount',
              })}
              name="planAccountId"
              rules={[defaultEmptyFieldError]}
            >
              <PlanAccountsSelect
                queryFilter={{
                  active: true,
                  destination: PlanAccountDestination.Revenue,
                  hierarchy: PlanAccountHierarchy.Analitical,
                }}
                disabled={isCanceled}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} sm={24} md={6}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'directSale.financial.form.documentType',
              })}
              name="documentTypeId"
              getValueFromEvent={(value) => {
                if (isStone) {
                  form.setFields([{ name: 'installmentQuantity', value: 1 }]);
                }
                return value;
              }}
              rules={[defaultEmptyFieldError]}
            >
              <DocumentTypesInfiniteSelectField
                queryFilter={{
                  isFinancial: true,
                  isStoneDocument: isStone,
                }}
                disabled={isCanceled}
              />
            </FloatingFormItem>
          </Col>

          {!companySetting?.isInstallmentFree && (
            <Col span={24} sm={24} md={6}>
              <FloatingFormItem
                label={intl.formatMessage({
                  id: 'directSale.financial.form.paymentCondition',
                })}
                name="paymentConditionId"
                rules={[defaultEmptyFieldError]}
              >
                <PaymentConditionInfiniteSelect
                  allowClear
                  style={{ width: '100%' }}
                  disabled={isCanceled}
                />
              </FloatingFormItem>
            </Col>
          )}

          <Col span={24} sm={24} md={6}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'directSale.financial.form.valueNegotiated',
              })}
              name="valueTotal"
              rules={[defaultEmptyFieldError]}
            >
              <InputDecimal
                max={
                  (totalValues?.totalDirectSale || 0) -
                  (totalValues?.totalDirectSalaeAccounts || 0)
                }
                disabled={isCanceled}
              />
            </FloatingFormItem>
          </Col>

          {!isStone && (
            <Col
              span={24}
              sm={24}
              md={6}
              hidden={!companySetting?.isInstallmentFree}
            >
              <FloatingFormItem
                label="Data de vencimento"
                name="installmentDue"
                initialValue={moment()}
              >
                <InputDate />
              </FloatingFormItem>
            </Col>
          )}

          <Col span={24} md={12} lg={6}>
            <FloatingFormItem name="costCenterId" label="Centro de custo">
              <CostCenterInfiniteSelectField />
            </FloatingFormItem>
          </Col>

          <Col
            span={24}
            sm={12}
            md={isStone ? 6 : 3}
            hidden={!companySetting?.isInstallmentFree}
          >
            <FloatingFormItem
              label="Qtd. Parcelas"
              name="installmentQuantity"
              initialValue={1}
              rules={
                isStone
                  ? [
                      {
                        type: 'number',
                        max: 18,
                        message: 'Limite de 18 parcelas',
                      },
                    ]
                  : []
              }
            >
              <InputDecimal
                min={0}
                max={isStone ? 18 : undefined}
                fractions={0}
                disabled={isStone && !isPaymentTypeCredit}
              />
            </FloatingFormItem>
          </Col>

          {!isStone && (
            <Col
              span={24}
              sm={12}
              md={3}
              hidden={!companySetting?.isInstallmentFree}
            >
              <FloatingFormItem
                label="Intervalo (dias)"
                name="installmentPeriod"
                initialValue={30}
              >
                <InputDecimal min={0} fractions={0} />
              </FloatingFormItem>
            </Col>
          )}

          <Col
            span={24}
            sm={12}
            md={isStone ? 6 : 3}
            hidden={!companySetting?.isInstallmentFree}
          >
            <FloatingFormItem
              label="Acres.(%)"
              name="increasePercent"
              initialValue={0}
              getValueFromEvent={(value = 0) => {
                const valueTotal = form.getFieldValue('valueTotal') || 0;

                form.setFieldValue(
                  'increaseAmount',
                  valueTotal * (value / 100),
                );
                return value;
              }}
            >
              <InputDecimal min={0} fractions={2} />
            </FloatingFormItem>
          </Col>

          <Col
            span={24}
            sm={12}
            md={isStone ? 6 : 3}
            hidden={!companySetting?.isInstallmentFree}
          >
            <FloatingFormItem
              label="Acres.(R$)"
              name="increaseAmount"
              initialValue={0}
              getValueFromEvent={(value = 0) => {
                const valueTotal = form.getFieldValue('valueTotal') || 0;

                form.setFieldValue(
                  'increasePercent',
                  (value * 100) / valueTotal,
                );
                return value;
              }}
            >
              <InputDecimal min={0} fractions={2} />
            </FloatingFormItem>
          </Col>

          <Col
            span={24}
            sm={12}
            md={isStone ? 6 : 3}
            hidden={!companySetting?.isInstallmentFree}
          >
            <FloatingFormItem
              label="Desc.(%)"
              name="decreasePercent"
              initialValue={0}
              getValueFromEvent={(value = 0) => {
                const valueTotal = form.getFieldValue('valueTotal') || 0;

                form.setFieldValue(
                  'decreaseAmount',
                  valueTotal * (value / 100),
                );
                return value;
              }}
            >
              <InputDecimal min={0} fractions={2} />
            </FloatingFormItem>
          </Col>

          <Col
            span={24}
            sm={12}
            md={isStone ? 6 : 3}
            hidden={!companySetting?.isInstallmentFree}
          >
            <FloatingFormItem
              label="Desc.(R$)"
              name="decreaseAmount"
              initialValue={0}
              getValueFromEvent={(value = 0) => {
                const valueTotal = form.getFieldValue('valueTotal') || 0;

                form.setFieldValue(
                  'decreasePercent',
                  (value * 100) / valueTotal,
                );
                return value;
              }}
            >
              <InputDecimal min={0} fractions={2} />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={6}>
            <FloatingFormItem label="Conta bancária" name="bankAccountId">
              <BankAccountInfiniteSelectField
                allowClear
                queryFilter={{ active: true }}
                disabled={!!documentTypeDetail.data?.bankAccountId}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={6}>
            <FloatingFormItem
              label="Número de autorização"
              name="authorizationNumber"
            >
              <Input />
            </FloatingFormItem>
          </Col>
        </Row>
      </Form>
    </>
  );
}

export default FinancialDirectSaleForm;
